// Generic inline ghost-text autocomplete for search inputs.
//
// Suggests the most-recent matching past query as a muted suffix behind the caret;
// Tab / → at end / tap on suffix accepts; Esc dismisses; clear-× appears when
// non-empty. Suppresses every browser-level suggestion popup (autofill, predictive
// text, password managers, spellcheck dictionary) via a belt-and-suspenders set
// of attributes — see init() body.
//
// Storage v2: localStorage[<historyKey>] = [{q: string, t: number}], where t is
// the last-used timestamp (Date.now ms). On read: dedupe by q (keep newest t),
// sort recency-desc. Cap default 500 (~50 KB). Single-page-app pages can pass
// their own historyKey to keep histories isolated.
//
// Usage:
//   window.SearchAutocomplete.init(inputEl, { historyKey: 'search_history_v2' });
//   window.SearchAutocomplete.saveQuery('hello', { historyKey: 'search_history_v2' });
//
// Save modes (controls what counts as a "commit"):
//   mode: 'auto'   (default) — saves on Enter and on blur (commit-on-leave). Good for
//                              quick search inputs where every typed query is meaningful.
//   mode: 'manual'           — never saves automatically. The page wires its own
//                              "useful action" signal (e.g. user opened a result file)
//                              and calls window.SearchAutocomplete.saveQuery() itself.
//                              Keeps history clean of noise from idle typing.
(function ($) {
    'use strict';

    const HISTORY_MAX = 500;

    function _now() { return Date.now(); }

    function _readRawV2(historyKey) {
        try {
            const raw = localStorage.getItem(historyKey);
            if (!raw) return null;
            const arr = JSON.parse(raw);
            return Array.isArray(arr) ? arr : null;
        } catch (_) { return null; }
    }
    function _migrateLegacy(historyKey, legacyKey) {
        // v1 was an array of strings ordered most-recent-first. Convert to v2 with
        // synthetic descending timestamps preserving original order; remove legacy key.
        if (!legacyKey) return [];
        try {
            const raw = localStorage.getItem(legacyKey);
            if (!raw) return [];
            const arr = JSON.parse(raw);
            if (!Array.isArray(arr)) return [];
            const base = _now();
            const migrated = arr
                .filter(x => typeof x === 'string' && x.trim())
                .map((q, i) => ({ q: q.trim(), t: base - i * 1000 }));
            try { localStorage.setItem(historyKey, JSON.stringify(migrated)); } catch (_) {}
            try { localStorage.removeItem(legacyKey); } catch (_) {}
            return migrated;
        } catch (_) { return []; }
    }

    function loadHistory(opts) {
        const o = opts || {};
        const historyKey = o.historyKey;
        const legacyKey = o.legacyKey;
        let arr = _readRawV2(historyKey);
        if (arr === null) arr = _migrateLegacy(historyKey, legacyKey);
        // Normalise + dedupe by q (keep newest timestamp) + sort recency-desc.
        const seen = Object.create(null);
        for (const raw of arr) {
            const q = String((raw && raw.q) || '').trim();
            const t = Number(raw && raw.t) || 0;
            if (!q) continue;
            if (!(q in seen) || seen[q] < t) seen[q] = t;
        }
        return Object.keys(seen)
            .map(q => ({ q: q, t: seen[q] }))
            .sort((a, b) => b.t - a.t);
    }
    function saveHistory(historyKey, list) {
        try { localStorage.setItem(historyKey, JSON.stringify(list)); } catch (_) {}
    }
    function saveQuery(q, opts) {
        const o = opts || {};
        if (!o.historyKey) return;
        q = (q || '').trim();
        if (!q) return;
        const max = Number(o.max) > 0 ? Number(o.max) : HISTORY_MAX;
        // Touch existing entry → bump its timestamp; or insert a new one.
        const list = loadHistory(o).filter(e => e.q !== q);
        list.unshift({ q: q, t: _now() });
        // Trim tail (oldest) — list is sorted desc, so pop drops the oldest entries.
        while (list.length > max) list.pop();
        saveHistory(o.historyKey, list);
    }
    function findSuggestion(prefix, opts) {
        const list = loadHistory(opts);
        if (!list.length) return '';
        // No prefix → suggest the most-recent query (list[0] after recency-desc sort).
        if (!prefix) return list[0].q;
        const lower = prefix.toLowerCase();
        // Prefix-match → return the most-recent match (first hit in recency order).
        for (const e of list) {
            if (e.q === prefix) continue;
            if (e.q.toLowerCase().startsWith(lower)) return e.q;
        }
        return '';
    }

    function init(inputEl, opts) {
        if (!inputEl || inputEl.__autocompleteInit) return;
        if (!opts || !opts.historyKey) {
            // Hard contract: caller must namespace history with a key.
            try { console.warn('[SearchAutocomplete] init() requires opts.historyKey'); } catch (_) {}
            return;
        }
        inputEl.__autocompleteInit = true;
        const $input = $(inputEl);

        // Suppress every browser-level suggestion popup we know about — predictive
        // text, autofill, password managers, spellcheck word lists. Belt-and-suspenders
        // because individual flags are honoured inconsistently across Chrome/Edge/Safari.
        $input.attr({
            // Plain `off` works in modern Chrome/Edge for non-credential fields; older
            // hacks with random tokens actually triggered Chrome to *show* generic
            // suggestions in some versions because unknown values fell back to default.
            autocomplete: 'off',
            autocorrect: 'off',
            autocapitalize: 'off',
            spellcheck: 'false',
            // No `name` → no key Chrome can correlate against saved form data.
            name: '',
            // Hints to keyboard / IME: it's a search field, prediction not needed.
            inputmode: 'search',
            // Password manager opt-outs (1Password, LastPass, Bitwarden, etc.).
            'data-form-type': 'other',
            'data-1p-ignore': 'true',
            'data-lpignore': 'true',
            'data-bwignore': 'true',
            // Accessibility: tell assistive tech we own the autocomplete UI.
            'aria-autocomplete': 'none'
        });
        // Empty `name` shows up as `name=""` in the DOM, which some PMs still see.
        try { inputEl.removeAttribute('name'); } catch (_) {}

        // Wrap input → ghost goes behind, both share the same layout box.
        const $parent = $input.parent();
        if (!$parent.hasClass('file-search-wrap')) {
            const $wrap = $('<div>', { class: 'file-search-wrap' });
            $input.before($wrap);
            $wrap.append($input);
        }
        const $wrap = $input.parent();
        const $ghost = $('<div>', { class: 'file-search-ghost', 'aria-hidden': 'true' });
        const $typed = $('<span>', { class: 'file-search-ghost__typed' });
        const $suffix = $('<span>', { class: 'file-search-ghost__suffix' });
        $ghost.append($typed).append($suffix);
        $wrap.prepend($ghost);

        // Mirror the input's computed font + box-model onto the ghost so __typed reserves
        // the exact pixel width occupied by typed text — otherwise the suffix lands at
        // a different x-offset than the caret AND at a different baseline (visible as
        // `t` and `est` rendered at different sizes / heights). Real inputs carry
        // size-modifier classes (Bootstrap fs-5, form-control-lg, etc.) that the CSS
        // file can't predict, so we read computed style at init. Borders matter: input
        // has `border: 1px solid …` inside its inset:0 box; ghost gets transparent
        // borders of identical width so its content-box ends at the same coordinates.
        function mirrorBox() {
            try {
                const cs = window.getComputedStyle(inputEl);
                $ghost.css({
                    'font-family': cs.fontFamily,
                    'font-size': cs.fontSize,
                    'font-weight': cs.fontWeight,
                    'font-style': cs.fontStyle,
                    'letter-spacing': cs.letterSpacing,
                    'word-spacing': cs.wordSpacing,
                    'line-height': cs.lineHeight,
                    'text-indent': cs.textIndent,
                    'padding-top': cs.paddingTop,
                    'padding-right': cs.paddingRight,
                    'padding-bottom': cs.paddingBottom,
                    'padding-left': cs.paddingLeft,
                    'border-top': cs.borderTopWidth + ' solid transparent',
                    'border-right': cs.borderRightWidth + ' solid transparent',
                    'border-bottom': cs.borderBottomWidth + ' solid transparent',
                    'border-left': cs.borderLeftWidth + ' solid transparent'
                });
            } catch (_) {}
        }
        mirrorBox();
        // Re-mirror once after browsers finalize layout (web-fonts may swap, Bootstrap
        // CSS may load after our script tag, container size-class tweaks may apply on
        // ResizeObserver-style flows). Cheap insurance against first-paint mismatch.
        setTimeout(mirrorBox, 0);

        // Standard clear-× button — appears at the right edge when input is non-empty.
        const $clear = $('<button>', {
            type: 'button',
            class: 'file-search-clear',
            'aria-label': 'Clear search',
            tabindex: '-1',
            html: '<i class="bi bi-x-lg"></i>'
        }).appendTo($wrap);
        function syncClearVisibility() {
            const hasVal = !!($input.val() || '').length;
            $wrap.toggleClass('has-value', hasVal);
        }
        $clear.on('click', function (e) {
            e.preventDefault();
            $input.val('');
            $input.trigger('input');
            paint();
            syncClearVisibility();
            $input.trigger('focus');
        });
        syncClearVisibility();

        let currentSuggestion = '';
        // Cache the input's placeholder once — we hide it whenever a ghost suggestion is
        // showing on an empty input (otherwise placeholder text overlaps the suggestion).
        const originalPlaceholder = inputEl.placeholder || '';

        function paint() {
            const value = $input.val() || '';
            const s = findSuggestion(value, opts);
            if (!s || s === value) {
                currentSuggestion = '';
                $typed.text('');
                $suffix.text('');
                inputEl.placeholder = originalPlaceholder;
                return;
            }
            currentSuggestion = s;
            $typed.text(value);
            $suffix.text(s.substring(value.length));
            // Suppress placeholder ONLY while empty (otherwise placeholder is already
            // hidden by the value itself, and we want to restore it on clear).
            inputEl.placeholder = value ? originalPlaceholder : '';
        }
        function clearGhost() {
            currentSuggestion = '';
            $typed.text('');
            $suffix.text('');
            inputEl.placeholder = originalPlaceholder;
        }
        function accept() {
            if (!currentSuggestion) return false;
            const typed = inputEl.value;
            const suffix = currentSuggestion.substring(typed.length);
            // Use execCommand('insertText') so the browser records the suffix as a
            // single undoable transaction — Ctrl+Z restores the typed prefix, like
            // native autocomplete behaves. Direct `.value =` overwrites without an
            // undo entry.
            let inserted = false;
            if (suffix) {
                try {
                    inputEl.focus();
                    const end = inputEl.value.length;
                    inputEl.setSelectionRange(end, end);
                    inserted = document.execCommand('insertText', false, suffix);
                } catch (_) { inserted = false; }
            } else {
                inserted = true;
            }
            if (!inserted) {
                $input.val(currentSuggestion);
            }
            $input.trigger('input');
            paint();
            return true;
        }

        const mode = opts.mode === 'manual' ? 'manual' : 'auto';
        $input.on('input', function () { paint(); syncClearVisibility(); });
        $input.on('focus', function () { paint(); });
        $input.on('blur', function () {
            // 'auto': commit-on-leave so quick searches make it into history without
            // requiring Enter. Also clear the ghost — quick search inputs are usually
            // submit-and-move-on, so leaving a stale suggestion behind reads as noise.
            //
            // 'manual': caller controls saving. Do NOT clear the ghost here — the search
            // page rerenders results frequently, and rendering work shifts focus off the
            // input transiently. If we cleared on blur, every render flicker would wipe
            // the suggestion (verified: blur→clear→refocus→paint→blur→clear pingpong
            // hides the suffix in DOM mid-frame). Suggestion stays visible across blurs;
            // it only disappears on Escape or when value no longer matches.
            if (mode === 'auto') {
                saveQuery($input.val(), opts);
                clearGhost();
            }
        });
        $input.on('keydown', function (e) {
            const atEnd = inputEl.selectionStart === inputEl.value.length
                       && inputEl.selectionEnd === inputEl.value.length;
            if (e.key === 'Tab' && currentSuggestion) {
                e.preventDefault();
                accept();
                return;
            }
            if (e.key === 'ArrowRight' && atEnd && currentSuggestion) {
                e.preventDefault();
                accept();
                return;
            }
            if (e.key === 'Enter') {
                if (mode === 'auto') saveQuery($input.val(), opts);
                return;
            }
            if (e.key === 'Escape' && currentSuggestion) {
                e.preventDefault();
                clearGhost();
            }
        });
        // Click on the ghost suffix region accepts (mobile/touch helper).
        $suffix.on('mousedown touchend', function (e) {
            if (!currentSuggestion) return;
            e.preventDefault();
            $input.trigger('focus');
            accept();
        });
    }

    // Generic API.
    window.SearchAutocomplete = { init: init, saveQuery: saveQuery };

    // Backward-compat shim: pre-bound to lectures-page keys so existing call sites
    // (`legacy/ui/lecture-filters.js`, `legacy/ui/helpers.js`) keep working without
    // having to know about the historyKey contract.
    //
    // mode: 'manual' on lectures — the only commit point is `openLecture()` (in
    // legacy/ui/helpers.js) which saves the current search query right before
    // navigation. Idle typing without opening a lecture stays out of history,
    // matching the search-page semantics ("save when query led to a useful action").
    const LECTURES_OPTS = {
        historyKey: 'lectures_search_history_v2',
        legacyKey: 'lectures_search_history',
        mode: 'manual'
    };
    window.AudioLectures = window.AudioLectures || {};
    window.AudioLectures.SearchAutocomplete = {
        init: function (el) { return init(el, LECTURES_OPTS); },
        saveQuery: function (q) { return saveQuery(q, LECTURES_OPTS); }
    };
})(jQuery);
